import React, {
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
} from "react";
import { FaRegPenToSquare } from "react-icons/fa6";

interface MultiInlineEditProps {
  children: React.ReactNode;
  displayValue: ReactNode;
  className?: string;
  displayClassName?: string;
  onSave: () => void;
  onCancel: () => void;
  onEditingChange?: (isEditing: boolean) => void;
  displayAs?: "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
}

const MultiInlineEdit: React.FC<MultiInlineEditProps> = ({
  children,
  displayValue,
  className = "",
  displayClassName = "",
  onSave,
  onCancel,
  onEditingChange,
  displayAs = "span",
  onFocus,
  onBlur,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  const handleCancel = useCallback(() => {
    onCancel();
    onBlur?.();
    setIsEditing(false);
  }, [onBlur, onCancel]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      onFocus?.();
      const target = event.target as Node;

      // Check if click is inside container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      // Check if click is inside a calendar (portaled to document.body)
      const calendarElement = (target as Element).closest?.(
        ".fixed.bg-white.border.border-gray-300.rounded-lg.shadow-lg",
      );
      if (calendarElement) {
        return;
      }

      // Check if the target is a select element or its dropdown
      const targetElement = target as Element;
      if (
        targetElement.tagName === "SELECT" ||
        targetElement.tagName === "OPTION"
      ) {
        return;
      }

      handleCancel();
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleCancel, isEditing, onFocus]);

  const handleSave = () => {
    onSave();
    onBlur?.();
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleOnClick = (e: React.MouseEvent) => {
    if (disabled) {
      return;
    }

    e.stopPropagation();
    onFocus?.();
    setIsEditing(true);
  };

  if (!isEditing) {
    const DisplayElement = displayAs;
    return (
      <DisplayElement
        onClick={handleOnClick}
        className={`flex flex-row gap-2 justify-between pr-2 items-center p-1 m-1 rounded transition-colors ${!disabled ? "hover:bg-secondary-light cursor-pointer" : "cursor-not-allowed"} ${displayClassName}`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {displayValue}
        <div>
          <FaRegPenToSquare
            className={`opacity-70 text-sm ${isHovering ? "" : "collapse"}`}
          />
        </div>
      </DisplayElement>
    );
  }

  return (
    <div
      className="flex flex-grow items-center justify-between bg-gray-50 p-2 rounded"
      onKeyDown={handleKeyDown}
      ref={containerRef}
    >
      <div className={`flex flex-wrap ${className}`}>{children}</div>
      <div className="flex gap-2 mx-2">
        <button
          type="button"
          onClick={handleSave}
          className="p-2 rounded bg-success text-white hover:bg-success-dark transition-colors"
          aria-label="Save"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="p-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 transition-colors"
          aria-label="Cancel"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export { MultiInlineEdit };
